/**
 * UserFormLibrary
 *
 * Phase 7: User-Facing Custom Form Library
 *
 * Displays the contractor's uploaded custom forms with actions to:
 * - Fill form with job data
 * - Edit field mappings
 * - Delete form
 * - Mark as favorite
 */

import { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  FileText,
  MoreVertical,
  Star,
  StarOff,
  Trash2,
  Edit,
  Download,
  Search,
  Plus,
  AlertCircle,
  CheckCircle2,
  Clock,
  Loader2,
  FileCheck,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useCustomForms,
  useDeleteCustomForm,
  useUpdateCustomForm,
  type CustomForm,
  type CustomFormFilters,
} from '@/hooks/useCustomForms';
import { FormFillDialog } from './FormFillDialog';
import { CustomFormUpload } from '@/components/settings/CustomFormUpload';
import { FormReviewWizard } from '@/components/settings/FormReviewWizard';

interface UserFormLibraryProps {
  jobId?: string;
  onFormFilled?: (pdfBase64: string, formName: string) => void;
  compact?: boolean;
}

export function UserFormLibrary({ jobId, onFormFilled, compact = false }: UserFormLibraryProps) {
  const [search, setSearch] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [fillFormId, setFillFormId] = useState<string | null>(null);
  const [reviewFormId, setReviewFormId] = useState<string | null>(null);
  const [deleteFormId, setDeleteFormId] = useState<string | null>(null);
  const [deleteFormPath, setDeleteFormPath] = useState<string>('');

  // Fetch forms - show approved forms by default, but also show forms needing review
  const filters: CustomFormFilters = search ? { search } : {};
  const { data: forms = [], isLoading, error } = useCustomForms(filters);

  const deleteForm = useDeleteCustomForm();
  const updateForm = useUpdateCustomForm();

  // Separate approved forms from forms needing review
  const approvedForms = forms.filter(f => f.review_status === 'approved');
  const pendingForms = forms.filter(f =>
    f.ai_analysis_status === 'completed' &&
    ['pending', 'in_progress'].includes(f.review_status)
  );
  const analyzingForms = forms.filter(f => f.ai_analysis_status === 'analyzing');

  const handleFillForm = (formId: string) => {
    setFillFormId(formId);
  };

  const handleReviewForm = (formId: string) => {
    setReviewFormId(formId);
  };

  const handleDeleteClick = (form: CustomForm) => {
    setDeleteFormId(form.id);
    setDeleteFormPath(form.pdf_storage_path);
  };

  const handleConfirmDelete = async () => {
    if (deleteFormId && deleteFormPath) {
      await deleteForm.mutateAsync({ id: deleteFormId, storagePath: deleteFormPath });
      setDeleteFormId(null);
      setDeleteFormPath('');
    }
  };

  const handleToggleFavorite = async (form: CustomForm) => {
    await updateForm.mutateAsync({
      id: form.id,
      input: { is_favorite: !form.is_favorite },
    });
  };

  const handleFormFilled = (pdfBase64: string, formName: string) => {
    setFillFormId(null);
    onFormFilled?.(pdfBase64, formName);
  };

  const getStatusBadge = (form: CustomForm) => {
    if (form.ai_analysis_status === 'analyzing') {
      return (
        <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          Analyzing
        </Badge>
      );
    }
    if (form.ai_analysis_status === 'failed') {
      return (
        <Badge variant="destructive">
          <AlertCircle className="h-3 w-3 mr-1" />
          Failed
        </Badge>
      );
    }
    if (form.review_status === 'approved') {
      return (
        <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Ready
        </Badge>
      );
    }
    if (['pending', 'in_progress'].includes(form.review_status)) {
      return (
        <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">
          <Clock className="h-3 w-3 mr-1" />
          Review
        </Badge>
      );
    }
    return null;
  };

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-destructive">
            <AlertCircle className="h-8 w-8 mx-auto mb-2" />
            <p>Failed to load forms</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      {!compact && (
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">My Forms</h3>
            <p className="text-sm text-muted-foreground">
              Upload and fill permit forms with job data
            </p>
          </div>
          <Button onClick={() => setUploadOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Upload Form
          </Button>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search forms..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Forms needing review */}
      {pendingForms.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-amber-800 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Needs Review ({pendingForms.length})
            </CardTitle>
            <CardDescription className="text-amber-700">
              Review AI field mappings before using
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingForms.map((form) => (
                <div
                  key={form.id}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-amber-600" />
                    <div>
                      <p className="font-medium text-sm">{form.form_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {form.fields?.length || 0} fields detected
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleReviewForm(form.id)}
                  >
                    Review
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Analyzing forms */}
      {analyzingForms.length > 0 && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-blue-800 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Analyzing ({analyzingForms.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analyzingForms.map((form) => (
                <div
                  key={form.id}
                  className="flex items-center gap-3 p-3 bg-white rounded-lg border"
                >
                  <FileText className="h-5 w-5 text-blue-600" />
                  <div className="flex-1">
                    <p className="font-medium text-sm">{form.form_name}</p>
                    <p className="text-xs text-muted-foreground">
                      AI is analyzing form fields...
                    </p>
                  </div>
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Approved forms */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-5 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : approvedForms.length === 0 && pendingForms.length === 0 && analyzingForms.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <FileCheck className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="font-semibold mb-2">No forms yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Upload a permit form PDF to get started
              </p>
              <Button onClick={() => setUploadOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Upload Form
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {approvedForms.map((form) => (
            <Card
              key={form.id}
              className={cn(
                'group hover:border-primary/50 transition-colors',
                form.is_favorite && 'border-amber-200'
              )}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="p-2 rounded-lg bg-muted">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-sm truncate">
                          {form.form_name}
                        </h4>
                        {form.is_favorite && (
                          <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400 flex-shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        {getStatusBadge(form)}
                        {form.jurisdiction_code && (
                          <Badge variant="secondary" className="text-xs">
                            {form.jurisdiction_code}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {form.fields?.length || 0} fields
                        {form.times_used > 0 && ` • Used ${form.times_used}x`}
                      </p>
                    </div>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleFillForm(form.id)}>
                        <Download className="h-4 w-4 mr-2" />
                        Fill Form
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleReviewForm(form.id)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Mappings
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleToggleFavorite(form)}>
                        {form.is_favorite ? (
                          <>
                            <StarOff className="h-4 w-4 mr-2" />
                            Remove Favorite
                          </>
                        ) : (
                          <>
                            <Star className="h-4 w-4 mr-2" />
                            Add to Favorites
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => handleDeleteClick(form)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Quick Fill Button */}
                <Button
                  className="w-full mt-3"
                  size="sm"
                  onClick={() => handleFillForm(form.id)}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Fill with Job Data
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      <CustomFormUpload
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
      />

      {/* Fill Dialog */}
      {fillFormId && (
        <FormFillDialog
          open={!!fillFormId}
          onClose={() => setFillFormId(null)}
          formId={fillFormId}
          jobId={jobId}
          onFilled={handleFormFilled}
        />
      )}

      {/* Review Wizard */}
      {reviewFormId && (
        <FormReviewWizard
          formId={reviewFormId}
          open={!!reviewFormId}
          onClose={() => setReviewFormId(null)}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteFormId} onOpenChange={() => setDeleteFormId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Form?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this form from your library.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteForm.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
